"use client";
import { useEffect, useRef, useState } from "react";
import Text from "@/components/Home/v2/shared/Text";
import classNames from "classnames";
import AiEditorCard from "./AiEditorCard";
import SocialConnectCard from "./SocialConnectCard";
import { AlternatingProductsItem } from "./AlternatingProductsSection";

export type StickyScrollProductsSectionData = {
  items: AlternatingProductsItem[];
};

const StickyScrollProductsSection = ({ items }: StickyScrollProductsSectionData) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Viewport count per card: first and last get 3, middle cards get 2
  const vpPerCard = items.map((_, i) =>
    i === items.length - 1 ? 3 : 2
  );
  // Cumulative scroll start (in vh units) for each card
  const cardStarts = vpPerCard.reduce<number[]>((acc, vp, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + vpPerCard[i - 1]);
    return acc;
  }, []);
  const totalVp = vpPerCard.reduce((a, b) => a + b, 0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const scrolledVh = -rect.top / window.innerHeight;

      if (scrolledVh <= 0) { setActiveIndex(0); return; }

      // Find the last card whose start threshold we've passed
      let idx = 0;
      for (let i = 1; i < items.length; i++) {
        if (scrolledVh >= cardStarts[i] - 0.05) idx = i;
      }
      setActiveIndex(idx);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [items.length]);

  return (
    // Outer wrapper — provides N viewports of scroll space
    <div
      ref={sectionRef}
      style={{ height: `${totalVp * 100}vh` }}
      className="relative"
    >
      {/* Sticky frame — stays pinned for the full scroll sequence */}
      <div
        className="sticky top-0 h-screen overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #F3F0FF 60%, #E8E4FD 100%)",
        }}
      >
        {items.map((item, idx) => {
          const isImageLeft = idx % 2 === 0;
          const isActive = activeIndex === idx;

          return (
            <div
              key={idx}
              className="absolute inset-0 flex items-center"
              style={{
                opacity: isActive ? 1 : 0,
                transform: isActive ? "translateY(0)" : "translateY(12px)",
                transition: "opacity 0.55s ease-in-out, transform 0.55s ease-in-out",
                pointerEvents: isActive ? "auto" : "none",
              }}
            >
              <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
                <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 md:items-center md:gap-16">

                  {/* ── Image / custom card slot ── */}
                  <div
                    className={classNames(
                      "relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[#F3F4F6]",
                      isImageLeft ? "md:order-1" : "md:order-2"
                    )}
                    style={{ border: "1px solid #E5E7EB" }}
                  >
                    {item.customSlot === "social-connect" ? (
                      <SocialConnectCard />
                    ) : item.customSlot === "ai-editor" ? (
                      <AiEditorCard />
                    ) : (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={item.image}
                        alt={item.imageAlt ?? ""}
                        className="absolute inset-0 h-full w-full object-contain p-6 md:p-8"
                      />
                    )}
                  </div>

                  {/* ── Text block ── */}
                  <div
                    className={classNames(
                      "flex flex-col gap-5",
                      isImageLeft ? "md:order-2" : "md:order-1"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#007F8C]" />
                      <Text
                        as="span"
                        variant="eyebrow"
                        className="text-[#007F8C]"
                        style={{ letterSpacing: "0.6px" }}
                      >
                        {item.eyebrow}
                      </Text>
                    </div>

                    <Text
                      as="h2"
                      variant="title-lg"
                      className="text-neutralBlack font-semibold -mt-1"
                    >
                      {item.title}
                    </Text>

                    <Text as="p" variant="body-lg" className="text-darkSlate04">
                      {item.description}
                    </Text>

                    <ul className="flex flex-col gap-3">
                      {item.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-[#22C55E]">
                            <svg
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="white"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <Text as="p" variant="body-md" className="text-darkSlate03">
                            {feature}
                          </Text>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-1">
                      <a
                        href="#"
                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#4E5DE0] to-[#5868F7] px-7 py-3 font-inter text-[15px] font-semibold text-white shadow-sm transition-transform hover:scale-[1.02]"
                      >
                        {item.ctaText || "Learn more"}
                      </a>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          );
        })}

        {/* ── Progress indicator ── */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {items.map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-400"
              style={{
                height: "6px",
                width: activeIndex === i ? "24px" : "6px",
                backgroundColor: activeIndex === i ? "#5868F7" : "#D1D5DB",
              }}
            />
          ))}
        </div>

        {/* ── Scroll hint (visible on first card only) ── */}
        <div
          className="absolute bottom-8 right-8 flex flex-col items-center gap-1 transition-opacity duration-400"
          style={{ opacity: activeIndex < items.length - 1 ? 0.45 : 0 }}
          aria-hidden
        >
          <svg width="16" height="22" viewBox="0 0 16 22" fill="none">
            <rect x="1" y="1" width="14" height="20" rx="7" stroke="#6B7280" strokeWidth="1.5"/>
            <rect x="7" y="5" width="2" height="5" rx="1" fill="#6B7280"/>
          </svg>
          <span className="text-[10px] text-gray-400 font-medium tracking-wide">SCROLL</span>
        </div>
      </div>
    </div>
  );
};

export default StickyScrollProductsSection;
